/**
 * PSR Phase 2 — per-source sentiment fetch cache.
 *
 * Hot path: in-memory (<50ms). Durable: Firestore `sentimentCache/{cacheKey}`.
 */
import type { SentimentFetcherSourceId, SentimentFetchResult } from '../../services/sentimentFetchers/types';
import { logAuditEvent } from '../telemetry/auditTelemetry';
import { loadAppStore } from '../appStore/appStorePort';

const FIRESTORE_COLLECTION = 'sentimentCache';

/** Per-source TTL (ms). Government stats refresh slowly; news is hotter. */
export const SENTIMENT_SOURCE_TTL_MS: Readonly<Record<SentimentFetcherSourceId, number>> = {
  'census-acs': 30 * 24 * 60 * 60 * 1000,
  bls: 14 * 24 * 60 * 60 * 1000,
  fcc: 30 * 24 * 60 * 60 * 1000,
  'local-public-data': 7 * 24 * 60 * 60 * 1000,
  'local-gis': 7 * 24 * 60 * 60 * 1000,
  'news-aggregator': 1 * 24 * 60 * 60 * 1000,
};

interface MemoryEntry {
  result: SentimentFetchResult;
  expiresAtMs: number;
}

interface FirestoreCacheRecord {
  result: SentimentFetchResult;
  expiresAt: string;
  fetchedAtMs: number;
  customerId?: string;
}

const memoryCache = new Map<string, MemoryEntry>();

function stripUndefined<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.filter(v => v !== undefined).map(v => stripUndefined(v)) as unknown as T;
  }
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (v === undefined) continue;
      out[k] = stripUndefined(v);
    }
    return out as T;
  }
  return value;
}

export function cacheKey(
  siteId: string,
  sourceId: SentimentFetcherSourceId,
  mode: string = 'feasibility',
): string {
  return `${siteId}|${sourceId}|${mode}`;
}

function ttlForSource(sourceId: SentimentFetcherSourceId): number {
  return SENTIMENT_SOURCE_TTL_MS[sourceId] ?? 7 * 24 * 60 * 60 * 1000;
}

async function resolveCurrentCustomerId(): Promise<string | undefined> {
  try {
    const useStore = await loadAppStore();
    const fromStore = (useStore.getState() as { companyId?: string | null }).companyId;
    if (typeof fromStore === 'string' && fromStore.trim()) return fromStore;
  } catch {
    // no-op
  }
  try {
    const { auth } = await import('../../firebase');
    const token = await auth.currentUser?.getIdTokenResult();
    const claim = token?.claims?.companyId;
    if (typeof claim === 'string' && claim.trim()) return claim;
  } catch {
    // no-op
  }
  return undefined;
}

function emitSentimentTelemetry(
  kind: 'sentiment-fetch-hit' | 'sentiment-fetch-miss' | 'sentiment-fetch-failed',
  siteId: string,
  sourceId: SentimentFetcherSourceId,
  extra?: Record<string, unknown>,
): void {
  logAuditEvent({
    type: kind,
    siteId,
    payload: { sourceId, ...extra },
  });
}

/** Test hook — clear in-memory layer. */
export function __resetSentimentMemoryCacheForTests(): void {
  memoryCache.clear();
}

export async function getCached(
  siteId: string,
  sourceId: SentimentFetcherSourceId,
  mode: string = 'feasibility',
): Promise<SentimentFetchResult | undefined> {
  const key = cacheKey(siteId, sourceId, mode);
  const now = Date.now();

  const mem = memoryCache.get(key);
  if (mem && mem.expiresAtMs > now) {
    emitSentimentTelemetry('sentiment-fetch-hit', siteId, sourceId, { layer: 'memory' });
    return { ...mem.result, cached: true };
  }

  try {
    const { db } = await import('../../firebase');
    const { doc, getDoc } = await import('firebase/firestore');
    const snap = await getDoc(doc(db, FIRESTORE_COLLECTION, key));
    if (!snap.exists()) {
      emitSentimentTelemetry('sentiment-fetch-miss', siteId, sourceId, { layer: 'firestore' });
      return undefined;
    }
    const data = snap.data() as Partial<FirestoreCacheRecord>;
    const expiresAtMs = data.expiresAt ? new Date(data.expiresAt).getTime() : 0;
    if (!data.result || !Number.isFinite(expiresAtMs) || expiresAtMs <= now) {
      emitSentimentTelemetry('sentiment-fetch-miss', siteId, sourceId, { layer: 'firestore', stale: true });
      return undefined;
    }
    const result = { ...data.result, cached: true };
    memoryCache.set(key, { result, expiresAtMs });
    emitSentimentTelemetry('sentiment-fetch-hit', siteId, sourceId, { layer: 'firestore' });
    return result;
  } catch (err) {
    emitSentimentTelemetry('sentiment-fetch-failed', siteId, sourceId, {
      phase: 'read',
      message: (err as Error)?.message,
    });
    return undefined;
  }
}

export async function setCached(
  siteId: string,
  sourceId: SentimentFetcherSourceId,
  result: SentimentFetchResult,
  mode: string = 'feasibility',
): Promise<void> {
  const key = cacheKey(siteId, sourceId, mode);
  const ttlMs = ttlForSource(sourceId);
  const expiresAtMs = Date.now() + ttlMs;
  const expiresAt = new Date(expiresAtMs).toISOString();
  const payload = stripUndefined({ ...result, cached: false });

  memoryCache.set(key, { result: payload, expiresAtMs });

  try {
    const { db } = await import('../../firebase');
    const { doc, setDoc } = await import('firebase/firestore');
    const record: FirestoreCacheRecord = {
      result: payload,
      expiresAt,
      fetchedAtMs: Date.now(),
      customerId: await resolveCurrentCustomerId(),
    };
    await setDoc(doc(db, FIRESTORE_COLLECTION, key), stripUndefined(record));
  } catch (err) {
    emitSentimentTelemetry('sentiment-fetch-failed', siteId, sourceId, {
      phase: 'write',
      message: (err as Error)?.message,
    });
  }
}

export async function invalidateCache(
  siteId: string,
  sourceId?: SentimentFetcherSourceId,
): Promise<number> {
  let removed = 0;
  const prefix = sourceId ? `${siteId}|${sourceId}|` : `${siteId}|`;
  for (const key of [...memoryCache.keys()]) {
    if (key.startsWith(prefix)) {
      memoryCache.delete(key);
      removed += 1;
    }
  }

  try {
    const { db } = await import('../../firebase');
    const { collection, getDocs, deleteDoc, doc } = await import('firebase/firestore');
    const snap = await getDocs(collection(db, FIRESTORE_COLLECTION));
    for (const d of snap.docs) {
      if (d.id.startsWith(prefix)) {
        await deleteDoc(doc(db, FIRESTORE_COLLECTION, d.id));
        removed += 1;
      }
    }
  } catch (err) {
    console.warn('[sentimentCache] invalidate failed:', (err as Error)?.message);
  }
  return removed;
}
